/**
 * Стандартні фільтри сторінок-списків документів (рішення власника
 * 2026-07-25, еталон — /employees): організація + статус + дати від/до.
 *
 * Дати: локальний фільтр сторінки МАЄ ПРІОРИТЕТ над глобальним «періодом
 * за замовчуванням» з шапки (порожньо — діє глобальний період).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { nomenclature as nomenclatureApi, contractors as contractorsApi, orgs } from '@/api/catalogs';
import DatePopover from '@/components/DatePopover';
import SearchSelect from '@/components/SearchSelect';
import { useDefaultPeriod, useDefaults } from '@/store/defaults';

export interface StatusOption {
  value: string;
  label: string;
}

export interface DocListFiltersState {
  orgId: string;
  status: string;
  dFrom: string;
  dTo: string;
  setOrgId: (v: string) => void;
  setStatus: (v: string) => void;
  setDFrom: (v: string) => void;
  setDTo: (v: string) => void;
  /** Готові date-параметри для API (локальні > глобальний період шапки). */
  dateFrom: string | undefined;
  dateTo: string | undefined;
  /** Частина queryKey — усі значення, що впливають на вибірку. */
  queryKey: (string | undefined)[];
}

export interface DocDateRangeState {
  dFrom: string;
  dTo: string;
  setDFrom: (v: string) => void;
  setDTo: (v: string) => void;
  dateFrom: string | undefined;
  dateTo: string | undefined;
}

/**
 * Лише дати документа: локальні поля сторінки порожні → діє «період за
 * замовчуванням» з шапки; заповнені → лише вони (ч.775, для сторінок зі
 * своїм набором інших фільтрів, як реєстр кадрових наказів).
 */
export function useDocDateRange(): DocDateRangeState {
  const [dFrom, setDFrom] = useState('');
  const [dTo, setDTo] = useState('');
  const { dateFrom: gFrom, dateTo: gTo } = useDefaultPeriod();
  const dateFrom = dFrom ? `${dFrom}T00:00:00` : gFrom;
  const dateTo = dTo ? `${dTo}T23:59:59` : gTo;
  return { dFrom, dTo, setDFrom, setDTo, dateFrom, dateTo };
}

/** Пара DatePopover «з — по» для рядка фільтрів (порожньо — період шапки). */
export function DocDateRange({ f, compact = false }: { f: DocDateRangeState; compact?: boolean }) {
  const st = compact ? { width: 108 } : undefined;
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flex: '0 0 auto' }}
      title="Дати документа (порожньо — період за замовчуванням з шапки)">
      <DatePopover value={f.dFrom} onChange={f.setDFrom} style={st} />
      <span style={{ color: 'var(--muted)' }}>—</span>
      <DatePopover value={f.dTo} onChange={f.setDTo} style={st} />
    </span>
  );
}

export function useDocListFilters(): DocListFiltersState {
  // Організація: доки фільтр не чіпали руками — слідуємо за «організацією
  // за замовчуванням» з шапки (2026-07-25, зауваження власника). Ручний
  // вибір (включно з «Усі організації») — override до кінця сесії сторінки.
  const defaultOrgId = useDefaults((s) => s.defaultOrgId);
  const [orgOverride, setOrgOverride] = useState<string | null>(null);
  const orgId = orgOverride ?? defaultOrgId;
  const [status, setStatus] = useState('');
  const { dFrom, dTo, setDFrom, setDTo, dateFrom, dateTo } = useDocDateRange();
  return {
    orgId, status, dFrom, dTo,
    setOrgId: (v) => setOrgOverride(v),
    setStatus, setDFrom, setDTo,
    dateFrom, dateTo,
    queryKey: [orgId, status, dateFrom, dateTo],
  };
}

const selStyleDefault = { width: 160, flex: '0 0 auto' } as const;

/**
 * Пошуковий фільтр за контрагентом (рішення власника 2026-07-31): комбобокс
 * SearchSelect «по парі букв» + ✕ скинути. Для рядка фільтрів списків
 * документів і звітів.
 */
export function ContractorFilter({ value, onChange, label = 'покупець', width = 260 }: {
  value: string;
  onChange: (id: string) => void;
  label?: string;
  width?: number;
}) {
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 2,
      width, flex: '0 0 auto' }}>
      <span style={{ flex: 1, minWidth: 0 }}>
        <SearchSelect
          items={(contractorsQ.data?.items ?? []).map((c) => ({
            id: c.id, label: c.name, hint: c.edrpou ?? null,
          }))}
          value={value}
          onChange={onChange}
          placeholder={`${label}: усі`}
        />
      </span>
      {value && (
        <button type="button" style={{ padding: '0 0.4rem' }}
          onClick={() => onChange('')} title="Скинути фільтр контрагента">×</button>
      )}
    </span>
  );
}

/**
 * Рядок фільтрів ПІД заголовком (рішення власника 2026-07-25): у рядку
 * заголовка лишається тільки назва сторінки + кнопка «+ Нове» справа,
 * фільтри — окремим рядком нижче.
 */
export function DocListFiltersRow({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '0.4rem',
      flexWrap: 'wrap', marginBottom: '1rem', marginTop: '-0.5rem',
    }}>
      {children}
    </div>
  );
}

/** Рендер фільтрів у .toolbar (після <h2>, перед кнопкою «+ Новий»). */
export default function DocListFilters({
  f,
  statusOptions,
  hideOrg = false,
  compact = false,
}: {
  f: DocListFiltersState;
  /** Опції статусу; порожній масив/undefined — селект статусу не показуємо. */
  statusOptions?: StatusOption[];
  hideOrg?: boolean;
  /** ч.792: вузькі поля — щоб увесь рядок фільтрів вмістився в один рядок. */
  compact?: boolean;
}) {
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const selStyle = compact ? { width: 130, flex: '0 0 auto' } as const : selStyleDefault;
  return (
    <>
      {!hideOrg && (
        <select value={f.orgId} onChange={(e) => f.setOrgId(e.target.value)} style={compact ? { width: 170, flex: '0 0 auto' } : selStyle}>
          <option value="">Усі організації</option>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
          ))}
        </select>
      )}
      {statusOptions && statusOptions.length > 0 && (
        <select value={f.status} onChange={(e) => f.setStatus(e.target.value)} style={selStyle}>
          {statusOptions.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      )}
      <DocDateRange f={f} compact={compact} />
    </>
  );
}


/**
 * Пошуковий фільтр за номенклатурою (рішення власника 2026-08-11 «хто
 * продав кран»): список документів звужується до тих, де товар є у рядках.
 */
export function NomenclatureFilter({ value, onChange }: {
  value: string;
  onChange: (id: string) => void;
}) {
  const nomQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomenclatureApi.listAll(),
  });
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 2,
      width: 260, flex: '0 0 auto' }}>
      <span style={{ flex: 1, minWidth: 0 }}>
        <SearchSelect
          items={(nomQ.data?.items ?? []).map((n) => ({
            id: n.id, label: n.name, hint: n.article ?? n.code ?? null,
          }))}
          value={value}
          onChange={onChange}
          placeholder="номенклатура: уся"
        />
      </span>
      {value && (
        <button type="button" style={{ padding: '0 0.4rem' }}
          onClick={() => onChange('')} title="Скинути фільтр номенклатури">×</button>
      )}
    </span>
  );
}
